import React, { useMemo } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import RenderDays from './renderDays';
import { getEthiopianMonthsInGregorianMonth, groupEthiopianMonthsByYear, toGeezNumber } from '../../../../../../platform/util/ethiopia/ethiopianCalendar';
import { useCalendar } from '../calendarContext';
import RenderEventListForMonth from './rederEventListForMonth';
import { colorNameToHex } from '../../../../../../platform/util/colorMap';
import { calendarMonthRange, calendarParts, ETHIOPIAN_MONTHS } from '../../../../../../platform/util/calendarSystem';


const StyledPaper = ({ children }) => (
  <Paper
    className="calendar-month-paper"
    elevation={0}
    style={{ 
      overflow: 'hidden', // Prevent overflow that causes scroll
      boxShadow: 'none',
      borderRadius: 0,
    }}
  >
    {children}
  </Paper>
);

const MonthView = ({ monthDate, showEventDetailsInside, showEventDetailsOutside, calculateHeight, printPreviewHeight }) => {
  const { bookingDatas, displayCalendar = 'gregory', displayClock } = useCalendar();
  const range = monthDate ? calendarMonthRange(format(monthDate, 'yyyy-MM-dd'), displayCalendar) : null;
  const ethParts = monthDate ? calendarParts(format(monthDate, 'yyyy-MM-dd'), 'ethiopic') : null;

  const mHeight = useMemo(() => {
    if (!calculateHeight || !monthDate) return null;

    const dpi = 96;
    let heightPx = window.innerHeight; // default fallback

    if (typeof printPreviewHeight === 'string') {
      const match = printPreviewHeight.trim().match(/^([0-9.]+)(in|cm)$/i);
      if (match) {
        const value = parseFloat(match[1]);
        const unit = match[2].toLowerCase();

        if (value === 0) {
          heightPx = window.innerHeight;
        } else if (unit === 'in') {
          heightPx = value * dpi;
        } else if (unit === 'cm') {
          heightPx = value * (dpi / 2.54);
        }
      }
    } else if (typeof printPreviewHeight === 'number' && printPreviewHeight > 0) {
      heightPx = printPreviewHeight;
    }

    const firstWeekday = new Date(`${range.start}T12:00:00`).getDay();
    const daysInMonth = Math.round((new Date(`${range.end}T12:00:00Z`) - new Date(`${range.start}T12:00:00Z`)) / 86400000) + 1;
    const calendarRows = Math.ceil((firstWeekday + daysInMonth) / 7);

    // Leave room for page padding, both headers, and the bilingual footer.
    // Use a little more of the remaining A4 height (about 2px per row in a
    // six-week month) without crowding the footer onto another page.
    const availableGridHeight = Math.max(350, heightPx - 213);
    return Math.max(64, Math.floor(availableGridHeight / calendarRows));
  }, [calculateHeight, monthDate, printPreviewHeight, range?.start, range?.end]);

  if (!monthDate) return null; 

  const { monthsWithYears = [] } = getEthiopianMonthsInGregorianMonth(monthDate);

  const grouped = groupEthiopianMonthsByYear(monthsWithYears);
  
  const monthDisplay = Object.entries(grouped)
    .map(([year, months]) => `${months.join(' - ')} ${year} | ${toGeezNumber(Number(year))}`)
    .join(' / ');
    const fontSize = Math.max(30, 36 - Math.floor(monthDisplay.length / 10)); // adjust range as needed

  return (
    <Grid
      container 
    >
      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
        <StyledPaper>
          {/* Header */}
          <Grid
            className="calendar-month-heading"
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, py: 2 }}
>
  {/* Gregorian Header */}
  <Grid item>
    <Typography
      className="calendar-header"
      sx={{
        fontSize: '2.75rem', // ⬅️ was 3rem
        fontWeight: 600,
        fontFamily: '"Inter", "Noto Sans", "Segoe UI", "Roboto", sans-serif',
        color: colorNameToHex.DarkBlue,  
      }}
    >
      {displayCalendar === 'ethiopic' ? `${ETHIOPIAN_MONTHS[ethParts.month - 1]} ${ethParts.year} E.C.` : format(monthDate, 'MMMM yyyy')}
    </Typography>
  </Grid>

  {/* Ethiopian or Other Calendar Header */}
  <Grid item>
    <Typography
      className="calendar-header"
      sx={{
        fontSize:  `${fontSize}px`, whiteSpace: 'nowrap', // ⬅️ was 3rem
        fontWeight: 600,
        fontFamily: '"Inter", "Noto Sans", "Segoe UI", "Roboto", sans-serif',
        color: colorNameToHex.DarkBlue,  
        textAlign: 'right',
      }}
    >
      {displayCalendar === 'ethiopic' ? `${range.start} – ${range.end} (Gregorian)` : monthDisplay}
    </Typography>
  </Grid>
</Grid>



          {/* Calendar Days */}
          <Grid container sx={{ width: '100%' }}>
            {RenderDays && RenderDays({ month: monthDate, bookingDatas, showEventDetailsInside, mHeight })}
          </Grid>

          {/* Events Outside Calendar */}
          {showEventDetailsOutside && RenderEventListForMonth && (
            <div>{RenderEventListForMonth(monthDate, bookingDatas, displayCalendar, displayClock)}</div>
          )}
        </StyledPaper>
      </Grid>
    </Grid>
  );
};

export default MonthView;
